import { get } from 'lodash';
import { normalizeMergeTagKey } from './mergeTagDictionary';

/**
 * Resolve a merge-tag colour expression (e.g. "{{user.brandPrimaryColor}}") to its
 * concrete value from the data source. Returns null if it isn't a resolvable tag.
 *
 * Used to render dynamic brand colours as real hex in the editor preview while the
 * saved model and the sent output keep the merge tag.
 */
export function resolveColorTagValue(
	tagExpression: string | undefined,
	dataSource?: Record<string, any>,
): string | null {
	const rawTag = String(tagExpression || '').trim();
	if (!rawTag || !dataSource) return null;

	const extractedKey = rawTag.match(/^\{\{\s*([^{}]+?)\s*\}\}$/)?.[1]?.trim() || rawTag;
	const normalizedKey = normalizeMergeTagKey(extractedKey);

	const value = get(dataSource, extractedKey) ?? get(dataSource, normalizedKey);
	if (typeof value === 'string' && value.trim().length > 0) {
		return value.trim();
	}

	return null;
}

/** Whether a value is a `{{ ... }}` merge-tag expression. */
export function isMergeTagExpression(value: string | undefined): boolean {
	return /^\{\{\s*[^{}]+?\s*\}\}$/.test(String(value || '').trim());
}

/**
 * Render-time helper for blocks that read colour values directly (rather than
 * through `getAdapterAttributesString`) — e.g. those that compose a `border`
 * shorthand by hand or feed colours into a Handlebars context.
 *
 * In preview (`testing`) mode a dynamic merge-tag colour is resolved to its real
 * hex from the data source so the canvas/raster shows the brand colour. In every
 * other mode (production/save) the original value is returned untouched, so the
 * saved model and sent output keep the `{{ ... }}` tag for send-time resolution.
 */
export function resolvePreviewColor(
	value: string | undefined,
	mode: string | undefined,
	dataSource?: Record<string, any>,
): string | undefined {
	if (mode !== 'testing' || !dataSource) return value;
	if (!isMergeTagExpression(value)) return value;
	return resolveColorTagValue(value, dataSource) || value;
}

// CSS props whose inline value can carry a dynamic RTE brand-colour merge tag.
const DYNAMIC_COLOR_VAR_PROPS = ['color', 'background-color'];

function setInlineStyleProp(node: HTMLElement, cssProp: string, value: string) {
	const style = node.getAttribute('style') || '';
	const re = new RegExp(`(^|;)\\s*${cssProp}\\s*:[^;]*`, 'i');
	const decl = `${cssProp}: ${value}`;
	node.setAttribute(
		'style',
		re.test(style)
			? style.replace(re, (_m, sep) => `${sep}${decl}`)
			: `${style ? `${style.replace(/;?\s*$/, '')}; ` : ''}${decl}`,
	);
}

/**
 * Re-derive the inline colour of RTE rich-text spans from the dynamic brand-colour
 * merge-tag KEY recorded on them.
 *
 * The toolbar's per-selection brand-colour picker records a brace-less key on the
 * wrapper span (e.g. `data-bay-color-var="user.brandPrimaryColor"`) while leaving a
 * resolved hex in the inline `style`. That hex is only a snapshot from when the colour
 * was applied, so every render re-derives the value from the key:
 *   - testing (canvas/preview): resolve to the CURRENT brand hex from `dataSource`, so a
 *     brand-colour change is reflected (matches block-level dynamic colours);
 *   - production: inject the raw `{{key}}` so the sent email re-colours per recipient.
 *     (CSSOM rejects `{{...}}`, so it is written via the style attribute string.)
 *
 * The key is kept brace-less in the saved model because a raw `{{...}}` in editor
 * content — even inside an attribute — is shattered by the merge-tag badge transform.
 * Used wherever RTE rich-text HTML is rendered (Text block, advanced table cells).
 */
export function applyDynamicColorVarsToHtml(
	html: string,
	mode: string | undefined,
	dataSource?: Record<string, any>,
): string {
	if (!html || html.indexOf('data-bay-') === -1) return html;
	if (typeof DOMParser === 'undefined') return html;
	const isProduction = mode === 'production';

	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
		const root = doc.body.firstElementChild as HTMLElement | null;
		if (!root) return html;

		DYNAMIC_COLOR_VAR_PROPS.forEach(cssProp => {
			const attr = `data-bay-${cssProp}-var`;
			root.querySelectorAll<HTMLElement>(`[${attr}]`).forEach(node => {
				const key = node.getAttribute(attr);
				if (!key) return;
				if (isProduction) {
					setInlineStyleProp(node, cssProp, `{{${key}}}`);
				} else {
					const resolved = resolveColorTagValue(`{{${key}}}`, dataSource);
					if (resolved) setInlineStyleProp(node, cssProp, resolved);
				}
			});
		});

		return root.innerHTML;
	} catch {
		return html;
	}
}
